/**
 * Whether a URL points to a file the browser can play with <video src>.
 *
 * @param {string|null|undefined} url
 * @returns {boolean}
 */
export function isDirectMediaUrl(url) {
  if (!url) return false

  try {
    return /\.(mp4|webm|ogg|m4v|mov|m3u8)(\?|$)/i.test(new URL(url).pathname)
  } catch {
    return false
  }
}

/**
 * Convert watch/share links into the best playable URL for iframe or <video>.
 *
 * @param {string|null|undefined} url
 * @returns {string|null}
 */
export function toEmbeddableVideoUrl(url) {
  if (!url || !String(url).trim()) return null

  const raw = String(url).trim()

  try {
    const parsed = new URL(raw)
    const host = parsed.hostname.replace(/^www\./, '').toLowerCase()

    if (host === 'aparat.com') {
      const hashFromPath = parsed.pathname.match(/\/v\/([a-zA-Z0-9]+)/)?.[1]
      const hashFromEmbed = parsed.pathname.match(/videohash\/([a-zA-Z0-9]+)/)?.[1]
      const hash = hashFromPath || hashFromEmbed
      if (hash) {
        return `https://www.aparat.com/video/video/embed/videohash/${hash}/vt/frame`
      }
    }

    if (
      host === 'youtube.com' ||
      host === 'm.youtube.com' ||
      host === 'youtube-nocookie.com'
    ) {
      const embedId = parsed.pathname.match(/\/embed\/([a-zA-Z0-9_-]+)/)?.[1]
      const shortsId = parsed.pathname.match(/\/shorts\/([a-zA-Z0-9_-]+)/)?.[1]
      const watchId = parsed.searchParams.get('v')
      const id = embedId || shortsId || watchId
      if (id) {
        return `https://www.youtube.com/embed/${id}`
      }
    }

    if (host === 'youtu.be') {
      const id = parsed.pathname.replace(/^\//, '').split('/')[0]
      if (id) {
        return `https://www.youtube.com/embed/${id}`
      }
    }

    if (host === 'vimeo.com' || host === 'player.vimeo.com') {
      const id =
        parsed.pathname.match(/\/video\/(\d+)/)?.[1] ||
        parsed.pathname.match(/\/(\d+)(?:\/|$)/)?.[1]
      if (id) {
        return `https://player.vimeo.com/video/${id}`
      }
    }
  } catch {
    return raw
  }

  return raw
}

/**
 * Resolve a video URL into a playable mode for Nova approval diffs.
 *
 * @param {string|null|undefined} url
 * @returns {{ mode: 'video'|'iframe', src: string }|null}
 */
export function resolveVideoPlayback(url) {
  const playable = toEmbeddableVideoUrl(url)
  if (!playable) return null

  return {
    mode: isDirectMediaUrl(playable) ? 'video' : 'iframe',
    src: playable,
  }
}
